import { useState, useEffect } from 'react'
import { X, Upload, FileText, Loader2, Image as ImageIcon, Layers, Star } from 'lucide-react'
import { rewriteApi, ExtractionPrompt } from '@/services/api'
import clsx from 'clsx'

interface SourceInputModalProps {
  open: boolean
  sourceText: string
  sourceTitle: string
  uploadingImage: boolean
  imageMethod: 'ocr' | 'api'
  imageModel: string
  selectedChannels: string[]
  onSetSourceText: (text: string) => void
  onSetSourceTitle: (title: string) => void
  onImageUpload: (file: File) => void
  onSetImageMethod: (method: 'ocr' | 'api') => void
  onSetImageModel: (model: string) => void
  onSectionSourcesExtracted?: (sections: Record<string, unknown>) => void
  onClose: () => void
}

export function SourceInputModal({
  open, sourceText, sourceTitle, uploadingImage,
  imageMethod, imageModel, selectedChannels,
  onSetSourceText, onSetSourceTitle, onImageUpload,
  onSetImageMethod, onSetImageModel, onSectionSourcesExtracted, onClose,
}: SourceInputModalProps) {
  const [tab, setTab] = useState<'text' | 'image'>('text')
  const [dragOver, setDragOver] = useState(false)

  // Extraction prompt state
  const [extractionPrompts, setExtractionPrompts] = useState<ExtractionPrompt[]>([])
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null)
  const [loadingPrompts, setLoadingPrompts] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [extractedSections, setExtractedSections] = useState<Record<string, string> | null>(null)

  // Load extraction prompts when modal opens and channels are selected
  useEffect(() => {
    if (open && selectedChannels.length > 0) {
      loadExtractionPrompts()
    }
  }, [open, selectedChannels])

  const loadExtractionPrompts = async () => {
    if (selectedChannels.length === 0) return
    setLoadingPrompts(true)
    // Load prompts for the first selected channel
    const res = await rewriteApi.extractionPrompts.list(selectedChannels[0])
    if (res.success && res.data) {
      setExtractionPrompts(res.data)
      // Auto-select default prompt
      const defaultPrompt = res.data.find(p => p.is_default === 1)
      if (defaultPrompt) {
        setSelectedPromptId(defaultPrompt.id)
      }
    }
    setLoadingPrompts(false)
  }

  const handleExtractWithPrompt = async (file: File) => {
    if (!selectedPromptId) {
      // Fall back to regular upload
      onImageUpload(file)
      return
    }

    setExtracting(true)
    try {
      const res = await rewriteApi.extractionPrompts.extract(selectedPromptId, file)
      if (res.success && res.data) {
        setExtractedSections(res.data.sections)
        // Set the raw text as main source
        onSetSourceText(res.data.rawText)
        // Notify parent about section-specific sources
        if (onSectionSourcesExtracted) {
          onSectionSourcesExtracted(res.data.sections)
        }
      } else {
        const errorMsg = res.error?.message || '알 수 없는 오류'
        alert(`텍스트 추출에 실패했습니다.\n\n오류: ${errorMsg}`)
      }
    } catch (err) {
      alert(`오류: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
    setExtracting(false)
  }

  const handleImageUpload = (file: File) => {
    if (selectedPromptId) {
      handleExtractWithPrompt(file)
    } else {
      onImageUpload(file)
    }
  }

  const selectedPrompt = extractionPrompts.find(p => p.id === selectedPromptId)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-bg-primary border border-border-subtle rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
          <h2 className="text-lg font-bold">원문 등록</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-bg-elevated text-text-tertiary hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-subtle">
          <button
            onClick={() => setTab('text')}
            className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-all ${
              tab === 'text' ? 'border-primary text-primary' : 'border-transparent text-text-tertiary hover:text-text-primary'
            }`}
          >
            <FileText className="w-4 h-4" /> 텍스트 입력
          </button>
          <button
            onClick={() => setTab('image')}
            className={`flex-1 py-2.5 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-all ${
              tab === 'image' ? 'border-primary text-primary' : 'border-transparent text-text-tertiary hover:text-text-primary'
            }`}
          >
            <ImageIcon className="w-4 h-4" /> 이미지 업로드
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'text' ? (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">소스 제목</label>
                <input
                  type="text"
                  value={sourceTitle}
                  onChange={(e) => onSetSourceTitle(e.target.value)}
                  placeholder="소스 콘텐츠 제목 (선택)"
                  className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-4 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">원본 콘텐츠 <span className="text-red-400">*</span></label>
                <textarea
                  value={sourceText}
                  onChange={(e) => onSetSourceText(e.target.value)}
                  placeholder="리라이팅할 원본 콘텐츠를 붙여넣으세요..."
                  rows={14}
                  className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-4 py-3 text-sm resize-y"
                />
                <p className="text-xs text-text-tertiary mt-1">{sourceText.length.toLocaleString()}자</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Extraction Prompt Selector */}
              {selectedChannels.length > 0 && (
                <div className="bg-bg-secondary/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5" /> 섹션별 추출 프롬프트
                    </label>
                    {loadingPrompts && <Loader2 className="w-3 h-3 animate-spin" />}
                  </div>

                  {extractionPrompts.length === 0 ? (
                    <p className="text-xs text-text-tertiary">
                      {loadingPrompts ? '로딩 중...' : '등록된 추출 프롬프트가 없습니다. 채널 설정에서 추가하세요.'}
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      <button
                        onClick={() => setSelectedPromptId(null)}
                        className={clsx(
                          'w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-2',
                          selectedPromptId === null
                            ? 'bg-bg-elevated border border-primary/50'
                            : 'hover:bg-bg-elevated border border-transparent'
                        )}
                      >
                        <span className="flex-1">기본 (전체 텍스트 추출)</span>
                      </button>
                      {extractionPrompts.map(prompt => (
                        <button
                          key={prompt.id}
                          onClick={() => setSelectedPromptId(prompt.id)}
                          className={clsx(
                            'w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-2',
                            selectedPromptId === prompt.id
                              ? 'bg-primary/10 border border-primary/50'
                              : 'hover:bg-bg-elevated border border-transparent'
                          )}
                        >
                          {prompt.is_default === 1 && <Star className="w-3 h-3 text-primary flex-shrink-0" />}
                          <span className="flex-1 truncate">{prompt.name}</span>
                          <span className="text-text-tertiary">
                            {Object.keys(JSON.parse(prompt.section_mapping)).length}섹션
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedPrompt && (
                    <p className="text-xs text-text-tertiary mt-2">
                      {selectedPrompt.description || '섹션별로 텍스트를 구분하여 추출합니다.'}
                    </p>
                  )}
                </div>
              )}

              {/* Method selector (shown when no extraction prompt selected) */}
              {!selectedPromptId && (
                <div className="flex items-center gap-3">
                  <div>
                    <label className="text-xs font-medium">추출 방식</label>
                    <select
                      value={imageMethod}
                      onChange={(e) => onSetImageMethod(e.target.value as 'ocr' | 'api')}
                      className="block mt-0.5 bg-bg-elevated border border-border-subtle rounded px-2 py-1 text-xs"
                    >
                      <option value="ocr">OCR</option>
                      <option value="api">AI Vision</option>
                    </select>
                  </div>
                  {imageMethod === 'api' && (
                    <div>
                      <label className="text-xs font-medium">모델</label>
                      <select
                        value={imageModel}
                        onChange={(e) => onSetImageModel(e.target.value)}
                        className="block mt-0.5 bg-bg-elevated border border-border-subtle rounded px-2 py-1 text-xs"
                      >
                        <option value="gemini-1.5-flash">Gemini Flash</option>
                        <option value="claude-sonnet-4-20250514">Claude Sonnet</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Upload zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setDragOver(false)
                  const file = e.dataTransfer.files[0]
                  if (file && file.type.startsWith('image/')) handleImageUpload(file)
                }}
                onClick={() => {
                  if (uploadingImage || extracting) return
                  const input = document.createElement('input')
                  input.type = 'file'
                  input.accept = 'image/*'
                  input.onchange = (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0]
                    if (file) handleImageUpload(file)
                  }
                  input.click()
                }}
                className={clsx(
                  'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all',
                  dragOver ? 'border-primary bg-primary/10' : 'border-border-subtle hover:border-primary/50',
                  (uploadingImage || extracting) && 'opacity-50 pointer-events-none'
                )}
              >
                {(uploadingImage || extracting) ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <p className="text-sm text-text-secondary">
                      {extracting ? '섹션별 텍스트 추출 중...' : '이미지에서 텍스트 추출 중...'}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-text-tertiary" />
                    <p className="text-sm text-text-secondary">이미지를 드래그하거나 클릭하여 업로드</p>
                    <p className="text-xs text-text-tertiary">
                      {selectedPromptId
                        ? '선택한 프롬프트로 섹션별 텍스트가 추출됩니다'
                        : '이미지에서 텍스트가 자동 추출됩니다'}
                    </p>
                  </div>
                )}
              </div>

              {/* Extracted sections preview */}
              {extractedSections && Object.keys(extractedSections).length > 0 && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <h4 className="text-xs font-bold text-green-400 mb-2">추출 완료!</h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {Object.entries(extractedSections).map(([section, data]) => {
                      // 객체인 경우 문자열로 변환
                      const displayText = typeof data === 'string'
                        ? data
                        : typeof data === 'object' && data !== null
                          ? Object.entries(data).map(([k, v]) =>
                              Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${v}`
                            ).join(' | ')
                          : String(data)
                      return (
                        <div key={section} className="text-xs">
                          <span className="font-medium text-primary">{section}:</span>
                          <p className="text-text-secondary mt-0.5 line-clamp-2">{displayText}</p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border-subtle flex justify-end">
          <button onClick={onClose} className="btn btn-primary">
            확인
          </button>
        </div>
      </div>
    </div>
  )
}
